/**
 * Pure helpers used by useChat — split into a separate module so they can be
 * unit-tested directly without driving the full React hook.
 */

import type { EmailDraftMode, EmailSearchResult } from '../../integrations/email/types';
import type { ChatMessage, Pill, WordOutline } from '../types';
import {
  outlineRowsToSections,
  outlineSectionsToMarkdown,
} from '../../../integrations/tools/word/services/wordDocument';
import {
  outlineAwareLabel,
  parseOutlineFromAi,
  parseReviewFindingsFromText,
  parseReviewListFindings,
  parseReviewSuggestions,
  parseWordResponse,
  stripWordPayloads,
  type ReviewFinding,
} from '../../../integrations/tools/word/utils/parseWordResponse';

// AIVA-5166 — a book-slot pill's chat_intent message is a machine-readable
// scheduling instruction (carries time_source / time_quote / verified_slot_id)
// that the backend needs verbatim but reads as raw technical parameters in the
// user's chat bubble. When a clicked pill carries those params, show the pill's
// own clean label (e.g. "Book Fri 3pm") as the bubble instead. The full message
// is still sent to the API unchanged — only what the user sees changes.
// Returns undefined for any other pill so its bubble is left untouched.
export function bookSlotBubbleOverride(message: string, label: string): string | undefined {
  const looksLikeBookSlot = /\btime_source=/.test(message) && /\btime_quote='slot /.test(message);
  return looksLikeBookSlot ? label : undefined;
}

// Deliberately the same two patterns the backend's `pill_click_parser.py` uses
// against the same string. Duplicated rather than shared because the string is
// the contract between the two: if the pill's message format ever changes, both
// sides must be updated together, and a silent mismatch here shows up as the
// booking loop this exists to prevent.
const BOOK_SLOT_START = /time_quote='slot (\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})'/;
const BOOK_SLOT_DURATION = /Schedule a (\d+)-minute meeting/;

/**
 * The slot a book-slot pill message represents, or `undefined` if it isn't one.
 *
 * AIVA-7482. The backend seeds its `verified_slots` from *this turn's* message,
 * so the evidence of a click lives for exactly one turn. Any question asked
 * between the click and the booking — the external-recipient confirmation is the
 * first — destroys it, and the submit is then refused for a time the user
 * demonstrably chose. Extracting it here lets the add-in re-send the slot until
 * the booking resolves, which is the client-owned-context pattern the resume
 * path already uses (ADR-chat-v2-hitl-resume-context).
 *
 * Only the two values the backend needs to rebuild the slot are returned; it
 * reconstructs the record and sets the user-intent flags itself, so nothing the
 * client sends can widen what a click is taken to mean.
 */
export function parseBookSlotConfirmation(
  message: string
): { startIso: string; durationMinutes: number } | undefined {
  const start = BOOK_SLOT_START.exec(message);
  if (!start?.[1]) return undefined;
  const duration = BOOK_SLOT_DURATION.exec(message);
  if (!duration?.[1]) return undefined;
  const durationMinutes = Number.parseInt(duration[1], 10);
  if (!Number.isFinite(durationMinutes) || durationMinutes <= 0) return undefined;
  return { startIso: start[1], durationMinutes };
}

export function stripAgentMetadata(text: string): string {
  return text
    .replace(/[ \t]{0,4}\(message_ids?=\[?[^)\]]{0,500}\]?\)/g, '')
    .replace(/[ \t]{1,4}([.!?,])/g, '$1')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

export function mergeSearchResultsById(
  existing: EmailSearchResult[],
  incoming: EmailSearchResult[]
): EmailSearchResult[] {
  const ids = new Set<string>();
  for (const r of existing) ids.add(r.id);
  const result = [...existing];
  for (const r of incoming) {
    if (!ids.has(r.id)) {
      ids.add(r.id);
      result.push(r);
    }
  }
  return result;
}

export function filterCalendarSourcesByMentions<T extends { subject?: string }>(
  sources: T[],
  assistantContent: string
): T[] {
  const content = assistantContent.toLowerCase();
  const filtered: T[] = [];
  for (const s of sources) {
    if (s.subject && content.includes(s.subject.toLowerCase())) {
      filtered.push(s);
    }
  }
  return filtered;
}

/**
 * Single source of truth for the Word pill surface: which backend pill tool
 * authorises which click handler, what the button says, and what it carries.
 * The handler sets, the authorisation map, and pill synthesis all derive from
 * this table — a new pill type is added HERE and nowhere else in this file.
 * (The handler strings themselves are a fixed contract with handlers.ts /
 * wordDocument.ts, which implement them as code.)
 */
interface WordPillSpec {
  /** Backend pill tool id, as it appears in the turn's allowed_tools. */
  readonly toolId: string;
  /** Frontend click-handler key (implemented in handlers.ts). */
  readonly handler: string;
  /** Button caption. */
  readonly label: string;
  /**
   * What the pill carries: plain text, table data, or the clipboard.
   *
   * "copy" is deliberately its own kind rather than "text": it carries the same
   * payload but writes to the clipboard, not the document, so it must stay out
   * of WORD_TEXT_PILL_HANDLERS — that set drives the placement logic (replace /
   * insert / append), and a Copy button is not a placement choice.
   */
  readonly kind: 'text' | 'table' | 'copy';
  /** Only offered while the user has a selection. */
  readonly needsSelection?: boolean;
}

/**
 * The Word placement pills, in the order the specification documents them
 * (§5, Variant C: Insert at cursor · Replace selection · Append to end · Copy).
 *
 * This table is now the single source of truth for ROW ORDER as well as labels
 * — see orderWordPills. Before that, order was an accident of injection
 * sequence: injectReplaceTextPill used `unshift` while the others used `push`,
 * so Replace selection led the row and the order changed depending on which
 * pills the model happened to emit itself. DE-01 AC-2 asks for four buttons
 * "in the documented order", which is not something injection order can promise.
 */
export const WORD_PILL_SPECS: readonly WordPillSpec[] = [
  {
    toolId: 'pill_word_insert_at_cursor',
    handler: 'word.insert_at_cursor',
    label: 'Insert at cursor',
    kind: 'text',
  },
  {
    toolId: 'pill_word_replace_selection',
    handler: 'word.replace_selection',
    label: 'Replace selection',
    kind: 'text',
    needsSelection: true,
  },
  { toolId: 'pill_word_append_to_end', handler: 'word.append_to_end', label: 'Append to end', kind: 'text' },
  { toolId: 'pill_word_insert_table', handler: 'word.insert_table', label: 'Insert table', kind: 'table' },
  { toolId: 'pill_word_copy_text', handler: 'word.copy_text', label: 'Copy', kind: 'copy' },
];

/**
 * Put the spec-table pills into their documented order.
 *
 * Only pills whose handler appears in WORD_PILL_SPECS are moved, and they are
 * permuted **among the slots they already occupy** — so pills this table does
 * not know about (apply_review, clear_highlights, bates, toc, …) keep their
 * position exactly. That matters: those pills carry their own payloads and
 * their relative order is decided by the skill that emitted them, not here.
 */
export function orderWordPills(pills: readonly Pill[]): Pill[] {
  const rank = new Map(WORD_PILL_SPECS.map((s, i) => [s.handler, i]));
  const out = [...pills];
  const slots: number[] = [];
  out.forEach((p, i) => {
    if (p.type === 'office_action' && rank.has(p.handler)) slots.push(i);
  });
  if (slots.length < 2) return out;
  const ordered = slots
    .map((i) => out[i] as Extract<Pill, { type: 'office_action' }>)
    .sort((a, b) => rank.get(a.handler)! - rank.get(b.handler)!);
  slots.forEach((slot, n) => {
    out[slot] = ordered[n]!;
  });
  return out;
}

/**
 * Variant B — an outline reply offers ONE placement button, plus Copy.
 *
 * The specification (§5) withholds Insert/Replace/Append from the outline
 * variant and shows a single `Insert outline (N)`. outlineAwareLabel already
 * produces that label; what was missing is the collapse, so an outline reply
 * rendered all three placements relabelled — "Insert outline (7)", "Replace
 * with outline (7)", "Append outline (7)" — which offers the same structural
 * insert three ways and makes the user choose between them for no reason.
 *
 * Replace selection is the one dropped first on purpose: overwriting a
 * selection with a whole document skeleton is the least likely intent and the
 * hardest to undo mentally, even though Ctrl+Z reverses it.
 */
export function collapseOutlinePills(pills: readonly Pill[], msgContent: string): Pill[] {
  const structural = pills.find((p) => p.type === 'office_action' && p.handler === 'word.insert_outline');
  if (structural) {
    const copyPill = pills.find((p) => p.type === 'office_action' && p.handler === 'word.copy_text');
    return copyPill ? [structural, copyPill] : [structural];
  }

  const payload = parseOutlineFromAi(msgContent);

  // AC-5: a payload we could not parse falls back to Variant C. Checked BEFORE
  // the markdown heuristic on purpose — a broken payload usually still contains
  // enough '#' lines to look like an outline, and letting the heuristic rescue
  // it would render an "Insert outline (N)" button whose N came from counting
  // headings the model never meant as the outline.
  if (payload.status === 'malformed') return [...pills];

  if (payload.status === 'ok') {
    // Variant B proper: ONE button carrying the parsed sections, plus Copy.
    // Built here rather than relabelling a text pill because the levels only
    // survive as structure — a text pill would flatten them back to '#' markers
    // and lose "title" (AC-2).
    const copy = pills.find((p) => p.type === 'office_action' && p.handler === 'word.copy_text');
    const insert: Pill = {
      type: 'office_action',
      label: `Insert outline (${payload.sections.length})`,
      handler: 'word.insert_outline',
      args: { sections: payload.sections },
    };
    return copy ? [insert, copy] : [insert];
  }

  // No payload at all — legacy markdown outlines (canned templates, older
  // skills) keep the heading-count collapse so they do not regress to a
  // four-button row while their prompts are updated.
  if (parseWordResponse(msgContent).type !== 'outline') return [...pills];
  const placements = new Set(['word.insert_at_cursor', 'word.replace_selection', 'word.append_to_end']);
  const keep =
    pills.find((p) => p.type === 'office_action' && p.handler === 'word.insert_at_cursor') ??
    pills.find((p) => p.type === 'office_action' && p.handler === 'word.append_to_end');
  if (!keep) return [...pills];
  return pills.filter((p) => p.type !== 'office_action' || !placements.has(p.handler) || p === keep);
}

/**
 * Variant A — build the Apply review bar from a parsed review payload.
 *
 * The corrections are the whole point of the reply, so the placement pills go:
 * a review reply's prose is the findings report, and pasting that over the
 * user's text is the AIVA-6914 failure. Copy and the other payload-bearing
 * pills stay.
 *
 * A pill the model ALREADY emitted wins. `pill_word_apply_review` is the same
 * list arriving by the tool-call route, and rebuilding it from the payload
 * would risk two Apply buttons disagreeing on the count.
 */
function buildReviewVariant(
  actionPills: readonly Pill[],
  findings: ReviewFinding[],
  msgContent: string,
  allowedTools?: string[]
): Pill[] {
  const kept = actionPills.filter(
    (p) =>
      !(
        p.type === 'office_action' &&
        // The per-term Replace all pills go with the placement pills. They are
        // the SAME edits this one button now carries, and leaving them would
        // offer the document-wide untracked overwrite alongside the reviewable
        // version of itself — with a count that means occurrences, not findings.
        (WORD_WRITE_PILL_HANDLERS.has(p.handler) || p.handler === 'word.replace_all')
      )
  );
  const alreadyEmitted = kept.some(
    (p) => p.type === 'office_action' && p.handler === 'word.apply_review_edits'
  );
  const apply: Pill = {
    type: 'office_action',
    label: `Apply review (${findings.length})`,
    handler: 'word.apply_review_edits',
    args: { findings },
  };
  const out = alreadyEmitted ? [...kept] : [apply, ...kept];

  // Variant A is "Apply review (N) + Copy" (§5). Copy is INJECTED rather than
  // assumed: the model emits its copy pill only sometimes, so relying on it made
  // the second half of the documented bar come and go between turns.
  //
  // The copied text has both payloads stripped. Without that, a reply carrying
  // {"review":[…]} would put the raw JSON on the clipboard — AC-4 keeps it out
  // of the thread, and pasting it into the document is the worse version of the
  // same mistake.
  injectCopyTextPill(out, toPlainPillText(stripWordPayloads(msgContent)), allowedTools);
  return orderWordPills(resolveWordPillConflicts(dedupePills(out)));
}

/** The review contract's findings, when this turn is allowed to act on them.
 *
 *  Returns null for every outcome that must NOT produce a button: no payload,
 *  a malformed one (AC-5 — fall back to the default bar), and a CLEAN one
 *  (AC-3 — `{"review":[]}` means the document is fine, so the reply is the
 *  answer and there is nothing to apply).
 *
 *  Gated on the declared tool for the same reason every other synthesis is: a
 *  skill that never declared the review pill must not grow one. An absent
 *  allowedTools means "old stream, we don't know" and stays permissive,
 *  matching maySynthesiseTablePills. */
function reviewPayloadFor(msgContent: string, allowedTools?: string[]): ReviewFinding[] | null {
  // `pill_word_replace_all` counts as declaring review edits: it is the write
  // tool the defined-terms skill actually grants, and what this bar applies is
  // strictly less destructive than what that tool does — comments and revisions
  // the reviewer can reject, instead of an untracked document-wide overwrite.
  // `pill_word_apply_review_edits` is the tool the review skills (/passive,
  // /clarity, /citations) actually declare, and it was missing from this gate:
  // it was added after the gate was written, so `reviewPayloadFor` returned null
  // for every one of those skills before looking at the reply at all. That is
  // why a /passive turn that skipped its pill could never have the bar
  // recovered (AIVA-7722).
  if (
    allowedTools &&
    !allowedTools.includes('pill_word_apply_review') &&
    !allowedTools.includes('pill_word_apply_review_edits') &&
    !allowedTools.includes('pill_word_replace_all')
  ) {
    return null;
  }
  const payload = parseReviewSuggestions(msgContent);
  if (payload.status === 'ok') return payload.findings;
  // A malformed or clean payload is a decision, not an absence — neither may be
  // second-guessed by re-reading the prose (AC-3, AC-5).
  if (payload.status !== 'none') return null;
  // No payload: recover the findings from the list the model wrote. See
  // parseReviewFindingsFromText for why this is the path that actually fires.
  const recovered = parseReviewFindingsFromText(msgContent);
  if (recovered.length > 0) return recovered;
  // The review-list shape the passive/clarity prompts specify. Last, because the
  // defined-term parser above is the more specific of the two.
  const fromList = parseReviewListFindings(msgContent);
  return fromList.length > 0 ? fromList : null;
}

const specByHandler = (handler: string): WordPillSpec | undefined =>
  WORD_PILL_SPECS.find((s) => s.handler === handler);

/** True when the turn's allowed_tools declares any pill type this file can
 *  synthesise or curate — derived from the spec table, not a name prefix.
 *
 *  Exported because the stream-time fallback in ``turnEvents`` needs the same
 *  test: it used to gate on a ``word_`` skill-key prefix plus a hand-maintained
 *  exclusion list, which silently mis-fired for every new skill that renders its
 *  own pills (e.g. word_paste_email → pill_word_insert_email). The backend's
 *  declared tool list is the authority, so both paths now ask it. */
export const declaresSynthesisablePill = (allowedTools: string[]): boolean =>
  WORD_PILL_SPECS.some((s) => allowedTools.includes(s.toolId));

/** Strip inline markdown decorations, keeping the '# ' and '- ' markers the
 *  Word insertion layer understands. Shared by curation and synthesis. */
function toPlainPillText(content: string): string {
  return content
    .replace(/\*\*(.*?)\*\*/gs, '$1')
    .replace(/\*(.*?)\*/gs, '$1')
    .replace(/`[^`]*`/g, '')
    .trim();
}

/**
 * Strip everything that is formatting rather than content: leading list and
 * heading markers, inline emphasis, and all whitespace differences. What is
 * left is the words the user actually read.
 */
function contentSignature(text: string): string {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim().replace(/^(?:[-*+]\s+|#{1,6}\s+|\d+[.)]\s+)/, ''))
    .join(' ')
    .replace(/[*_`]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

export const WORD_TEXT_PILL_HANDLERS = new Set(
  WORD_PILL_SPECS.filter((s) => s.kind === 'text').map((s) => s.handler)
);

/** The pills whose `text` arg is what lands in the document (or the clipboard). */
const WORD_PAYLOAD_PILL_HANDLERS = new Set(['word.copy_text', ...WORD_TEXT_PILL_HANDLERS]);

/**
 * Re-align a model-emitted pill's payload with the message the user read.
 *
 * The bubble renders the assistant MESSAGE; the button inserts the pill's own
 * `text` argument. The model authors both, and they drift. A /summarise reply
 * showed three bullets in chat, but its pill argument had dropped the "- " from
 * the last one — so Word received two bullets and a stray flush-left paragraph.
 * The insertion pipeline was faithful; it was handed different text.
 *
 * The swap is deliberately narrow: it fires ONLY when both strings carry the
 * same words once markers, emphasis, case and whitespace are normalised away —
 * that is, when the sole difference is formatting. A pill that carries genuinely
 * different text (a subset of the reply, a reworded draft, a placeholder) has a
 * different signature and is left exactly as the model wrote it.
 *
 * The message wins rather than the argument because the message is what the
 * user saw and approved before clicking.
 */
function reconcilePayloadPillsWithMessage(pills: Pill[], plainText: string): Pill[] {
  if (!plainText.trim()) return pills;
  const target = contentSignature(plainText);
  if (!target) return pills;
  return pills.map((p) => {
    if (p.type !== 'office_action' || !WORD_PAYLOAD_PILL_HANDLERS.has(p.handler)) return p;
    const current = p.args.text;
    if (typeof current !== 'string' || current === plainText) return p;
    if (contentSignature(current) !== target) return p;
    return { ...p, args: { ...p.args, text: plainText } };
  });
}

/** True when the turn's allowed_tools declares a TEXT placement pill.
 *
 *  `allowed_tools` IS the per-skill table, sent by the backend every turn, so the
 *  add-in needs no list of its own. This reproduces the three hand-written
 *  skill-key exclusions exactly, without naming any of them:
 *    - word_table declares only insert_table + copy → no text pill → excluded
 *    - word_passive / word_clarity declare only apply_review + clear_highlights
 *      + copy → no text pill → excluded
 *  and it also excludes the ones the hand list MISSED (word_gaps, word_risks,
 *  word_track_start, word_track_stop), which declare no placement pill at all and
 *  were being offered "Insert at cursor" under a report. */
export const declaresTextPill = (allowedTools: readonly string[]): boolean =>
  WORD_PILL_SPECS.some((spec) => spec.kind === 'text' && allowedTools.includes(spec.toolId));

export const WORD_TABLE_PILL_HANDLERS = new Set(
  WORD_PILL_SPECS.filter((s) => s.kind === 'table').map((s) => s.handler)
);

// Handlers whose pill pastes response content INTO the document — text inserts
// and table inserts. None of these may appear on a findings / report reply or
// on an error / guidance nudge: there is nothing to insert, and clicking one
// would overwrite the user's selection with the report or the error notice
// (AIVA-6914). Payload-bearing review pills (apply_review, insert_comment,
// highlight, replace_all, …) are deliberately excluded — they carry real
// corrections the user still wants — as is Copy, which never mutates the doc.
export const WORD_WRITE_PILL_HANDLERS = new Set<string>([
  ...WORD_TEXT_PILL_HANDLERS,
  ...WORD_TABLE_PILL_HANDLERS,
]);

function parseMdTableFromLines(lines: string[]): { headers: string[]; rows: string[][] } | null {
  const parseRow = (l: string): string[] =>
    l
      .split('|')
      .slice(1, -1)
      .map((c) => c.trim());
  for (let i = 0; i < lines.length - 1; i++) {
    const line = lines[i]!;
    const next = lines[i + 1] ?? '';
    if (!line.trim().startsWith('|') || !/^\|[\s\-:|]+\|/.test(next.trim())) continue;
    const headers = parseRow(line);
    const rows: string[][] = [];
    for (let j = i + 2; j < lines.length; j++) {
      const row = lines[j]!;
      if (!row.trim().startsWith('|')) break;
      rows.push(parseRow(row));
    }
    if (headers.length > 0) return { headers, rows };
  }
  return null;
}

export function resolveTablePillData(
  pill: Extract<Pill, { type: 'office_action' }>,
  assistantContent: string
): { headers: string[]; rows: string[][] } | null {
  const existingHeaders = pill.args.headers;
  if (Array.isArray(existingHeaders) && existingHeaders.length > 0) {
    return {
      headers: existingHeaders.map(String),
      rows: (Array.isArray(pill.args.rows) ? (pill.args.rows as unknown[][]) : []).map((r) =>
        (Array.isArray(r) ? r : []).map(String)
      ),
    };
  }
  return parseMdTableFromLines(assistantContent.split('\n'));
}

export function extractFirstWordPillText(renderedPills: Pill[], wordTextHandlers: Set<string>): string {
  const first = renderedPills.find(
    (p): p is Extract<Pill, { type: 'office_action' }> =>
      p.type === 'office_action' && wordTextHandlers.has(p.handler)
  );
  return typeof first?.args?.text === 'string' ? first.args.text.trim() : '';
}

export function fillWordPill(p: Pill, plainText: string, assistantContent: string): Pill {
  if (p.type !== 'office_action') return p;
  if (WORD_TEXT_PILL_HANDLERS.has(p.handler) && !p.args?.text) {
    return { ...p, args: { ...p.args, text: plainText } };
  }
  if (
    WORD_TABLE_PILL_HANDLERS.has(p.handler) &&
    !(Array.isArray(p.args?.headers) && (p.args.headers as unknown[]).length > 0)
  ) {
    const tableData = resolveTablePillData(p, assistantContent);
    if (tableData) {
      return { ...p, args: { ...p.args, headers: tableData.headers, rows: tableData.rows } };
    }
  }
  return p;
}

export function applyWordPillsFill(
  prev: ChatMessage[],
  plainText: string,
  assistantContent: string
): ChatMessage[] {
  const updated = [...prev];
  for (let i = updated.length - 1; i >= 0; i--) {
    const m = updated[i];
    if (m?.role !== 'assistant' || !m.actionPills) continue;
    updated[i] = {
      ...m,
      actionPills: m.actionPills.map((p) => fillWordPill(p, plainText, assistantContent)),
    };
    return updated;
  }
  return prev;
}

// Map handler → the backend tool name in `allowedTools` that authorises it.
// If a handler's authoriser isn't in the passed allowedTools list, that pill
// must NOT be synthesised — the skill deliberately excluded it (e.g.
// /grammar declares only pill_word_replace_selection). Derived from
// WORD_PILL_SPECS — never edited by hand.
// Text AND copy pills are both authorised from the turn's allowed_tools, so a
// pill is only ever synthesised when the skill actually declares its tool.
const HANDLER_TO_ALLOWED_TOOL: Record<string, string> = Object.fromEntries(
  WORD_PILL_SPECS.filter((s) => s.kind === 'text' || s.kind === 'copy').map((s) => [s.handler, s.toolId])
);

function isAuthorised(handler: string, allowedTools: string[] | undefined): boolean {
  // Undefined means "we don't know" (older stream, resume path) — fall back
  // to the pre-flag behaviour of injecting the standard set.
  if (allowedTools === undefined) return true;
  const authoriser = HANDLER_TO_ALLOWED_TOOL[handler];
  if (!authoriser) return true;
  return allowedTools.includes(authoriser);
}

/** Build a text pill for `handler` from its spec — labels/handlers come from
 *  WORD_PILL_SPECS, never from string literals at the call sites. */
function textPillFor(handler: string, text: string): Pill | null {
  const spec = specByHandler(handler);
  if (!spec) return null;
  // Outline-aware, like the two other paths that build placement pills — the
  // pill-tool emitter (emitTextPill) and the stream-time fallback both run
  // their labels through this. Synthesised pills did not, so the SAME outline
  // reply read "Insert outline (7)" when the model called the pill tool and a
  // bare "Insert at cursor" when it did not. The label is what tells the user
  // how much is about to land in the document; it cannot depend on which
  // internal path produced the button.
  return {
    type: 'office_action',
    label: outlineAwareLabel(spec.label, text),
    handler: spec.handler,
    args: { text },
  };
}

export function injectReplaceTextPill(
  enriched: Pill[],
  plainTextFallback: string,
  allowedTools?: string[]
): void {
  if (!isAuthorised('word.replace_selection', allowedTools)) return;
  const alreadyHasReplace = enriched.some(
    (p) => p.type === 'office_action' && p.handler === 'word.replace_selection'
  );
  if (alreadyHasReplace) return;
  const ref = enriched.find((p) => p.type === 'office_action' && WORD_TEXT_PILL_HANDLERS.has(p.handler)) as
    Extract<Pill, { type: 'office_action' }> | undefined;
  if (!ref) return;
  const text = (ref.args.text as string | undefined) || plainTextFallback;
  const pill = textPillFor('word.replace_selection', text);
  if (pill) enriched.unshift(pill);
}

export function injectInsertAndAppendPills(
  enriched: Pill[],
  plainTextFallback: string,
  allowedTools?: string[]
): void {
  const ref = enriched.find((p) => p.type === 'office_action' && WORD_TEXT_PILL_HANDLERS.has(p.handler)) as
    Extract<Pill, { type: 'office_action' }> | undefined;
  if (!ref) return;
  const text = (ref.args.text as string | undefined) || plainTextFallback;
  for (const handler of ['word.insert_at_cursor', 'word.append_to_end']) {
    const alreadyThere = enriched.some((p) => p.type === 'office_action' && p.handler === handler);
    if (alreadyThere || !isAuthorised(handler, allowedTools)) continue;
    const pill = textPillFor(handler, text);
    if (pill) enriched.push(pill);
  }
}

/**
 * Add the Copy button when the skill declares ``pill_word_copy_text``.
 *
 * Authorised through the same ``allowedTools`` gate as every other pill — the
 * grant lives in the skill document, not in a frontend special case, so which
 * skills offer Copy is a seed-data decision like all the others. Label and
 * handler come from WORD_PILL_SPECS rather than string literals here.
 */
export function injectCopyTextPill(
  enriched: Pill[],
  plainTextFallback: string,
  allowedTools?: string[]
): void {
  const spec = WORD_PILL_SPECS.find((s) => s.kind === 'copy');
  if (!spec) return;
  if (!isAuthorised(spec.handler, allowedTools)) return;
  const alreadyHasCopy = enriched.some((p) => p.type === 'office_action' && p.handler === spec.handler);
  if (alreadyHasCopy) return;
  // Prefer the text a write pill already carries (it has been through the
  // same resolution) and fall back to the rendered response.
  const ref = enriched.find((p) => p.type === 'office_action' && WORD_TEXT_PILL_HANDLERS.has(p.handler)) as
    Extract<Pill, { type: 'office_action' }> | undefined;
  const text = (ref?.args.text as string | undefined) || plainTextFallback;
  if (!text) return;
  enriched.push({ type: 'office_action', label: spec.label, handler: spec.handler, args: { text } });
}

const TABLE_POSITION_ORDER = ['at_cursor', 'replace_selection', 'append_to_end'];

/** Order the table row the way orderWordPills orders the text row.
 *  orderWordPills ranks by HANDLER, and all three table pills share one, so it
 *  leaves them in whatever order they were built — which put Replace table
 *  first purely because injectReplaceTablePill unshifts. */
export function orderWordTablePills(pills: Pill[]): Pill[] {
  const rank = (p: Pill): number => {
    if (p.type !== 'office_action') return TABLE_POSITION_ORDER.length + 1;
    if (!isWordTablePill(p)) return TABLE_POSITION_ORDER.length; // Copy sits last
    const i = TABLE_POSITION_ORDER.indexOf(String(p.args?.insert_position ?? 'at_cursor'));
    return i === -1 ? 0 : i;
  };
  return [...pills].sort((a, b) => rank(a) - rank(b));
}

export function injectReplaceTablePill(enriched: Pill[]): void {
  const alreadyHasTableReplace = enriched.some(
    (p) =>
      p.type === 'office_action' &&
      p.handler === 'word.insert_table' &&
      p.args?.insert_position === 'replace_selection'
  );
  if (alreadyHasTableReplace) return;
  const ref = enriched.find((p) => p.type === 'office_action' && WORD_TABLE_PILL_HANDLERS.has(p.handler)) as
    Extract<Pill, { type: 'office_action' }> | undefined;
  if (!ref) return;
  enriched.unshift({
    type: 'office_action',
    label: 'Replace table',
    handler: 'word.insert_table',
    args: { ...ref.args, insert_position: 'replace_selection' },
  });
}

/** Whether this pill can only act on a selection.
 *
 *  Normally the spec answers that, but the three table pills SHARE the handler
 *  `word.insert_table` and differ only in `insert_position`, so a lookup keyed
 *  on handler cannot tell Replace table from Insert table here. That is why the
 *  table branch used to skip markPillsNeedingSelection entirely and hide the
 *  Replace pill instead — the reported inconsistency: Replace selection greys
 *  out when you deselect, Replace table vanished (AIVA-8038).
 */
export function pillNeedsSelection(pill: Pill): boolean {
  if (pill.type !== 'office_action') return false;
  if (isWordTablePill(pill)) return pill.args?.insert_position === 'replace_selection';
  return specByHandler(pill.handler)?.needsSelection === true;
}

export function isWordTablePill(p: Pill): boolean {
  return p.type === 'office_action' && WORD_TABLE_PILL_HANDLERS.has(p.handler);
}

// Handlers that write to one fixed target, so a second pill with the same
// handler is either a duplicate or two conflicting payloads for the same
// spot — only the first survives. word.replace_all is deliberately absent:
// it legitimately repeats with a different `find` per term (/terms flow).
const SINGLE_INSTANCE_WORD_HANDLERS = new Set([
  'word.replace_selection',
  'word.apply_review',
  'word.clear_highlights',
  'word.insert_toc',
]);

/**
 * Resolve conflicting Word pills from a multi-intent turn. Precedence:
 * targeted edits (`word.apply_review`) beat a wholesale selection replace —
 * both write to the selection with different content, and the targeted set
 * is what the findings list in chat described. Fixed-target handlers are
 * capped at one pill each.
 */
export function resolveWordPillConflicts(pills: Pill[]): Pill[] {
  const hasApplyReview = pills.some((p) => p.type === 'office_action' && p.handler === 'word.apply_review');
  const seenOnce = new Set<string>();
  const out: Pill[] = [];
  for (const p of pills) {
    if (p.type === 'office_action') {
      if (hasApplyReview && p.handler === 'word.replace_selection') continue;
      if (SINGLE_INSTANCE_WORD_HANDLERS.has(p.handler)) {
        if (seenOnce.has(p.handler)) continue;
        seenOnce.add(p.handler);
      }
    }
    out.push(p);
  }
  return out;
}

/** What makes two pills "the same pill" for dedupe purposes.
 *
 *  An office_action carrying an explicit id is keyed on THAT, not on its label
 *  (AIVA-7956). The label is a display string — truncated, rounded — so two
 *  genuinely different things can share one, and here that meant a real email
 *  disappearing from the paste-email picker: 15 candidates rendered as 6 pills.
 *  Only paste-email sets `email_id` today, so every other office_action keeps
 *  the label-based key it has always had. */
function pillDedupeKey(p: Pill): string {
  if (p.type === 'office_action') {
    const pos = typeof p.args?.insert_position === 'string' ? p.args.insert_position : '';
    const id = typeof p.args?.email_id === 'string' ? p.args.email_id : '';
    return id ? `office|${p.handler}|${pos}|id:${id}` : `office|${p.handler}|${pos}|${p.label}`;
  }
  if (p.type === 'name_candidate') return `candidate|${p.id}`;
  return `intent|${p.message}`;
}

export function dedupePills(pills: Pill[]): Pill[] {
  const seen = new Set<string>();
  const out: Pill[] = [];
  for (const p of pills) {
    const key = pillDedupeKey(p);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(p);
  }
  return out;
}

/**
 * Seeded into an empty chat input on the FIRST name-candidate pick, but ONLY
 * on a calendar_assistant turn (see useChat.handlePillClick's skillKey
 * check) — find_user_by_name's disambiguation pill also fires on
 * email_compose, email_draft, send_attachment_compose and email_search, and
 * this sentence would be wrong on any of those ("reply to Jawahar" is not
 * "set up meeting with Jawahar"). Never overrides text the user already
 * typed; only stands in for a truly empty box.
 */
export const NAME_CANDIDATE_LEAD_IN = 'Set up meeting with ';

/**
 * Join selected names the way a person would write them in a sentence:
 * "Ana", "Ana and Bo", "Ana, Bo and Cy". Empty input returns "" so callers
 * can treat "nothing selected" and "remove the name-list text" the same way.
 */
export function joinNamesNaturally(names: readonly string[]): string {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0]!;
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** Character range in the chat input currently occupied by the spliced-in name list. */
export interface NameListAnchor {
  start: number;
  end: number;
}

/**
 * Build the hidden metadata suffix that carries selected candidates'
 * resolved email addresses onto the ACTUAL sent turn, while the visible chat
 * bubble stays name-only (see useChat.handleSend, which passes this
 * appended text as `messageBody` and the clean typed sentence as
 * `displayText` — the same split the codebase already uses for e.g. a
 * slash command's expanded prompt or an attachment's extracted text).
 *
 * Only candidates that actually resolved to an address are listed — a
 * pill can carry no `mail` (an older backend, or Graph returning none), and
 * silently dropping those is right: there's nothing to hand the model that
 * it doesn't already have from the name alone. Returns "" when nobody in
 * the selection has one, so callers can append it unconditionally.
 */
export function buildResolvedContactsSuffix(entries: readonly { name: string; mail?: string }[]): string {
  const withMail = entries.filter((e): e is { name: string; mail: string } => !!e.mail);
  if (withMail.length === 0) return '';
  const list = withMail.map((e) => `${e.name}: ${e.mail}`).join('; ');
  return `\n\n(Resolved contacts — ${list}. Use these addresses directly; do not look them up again.)`;
}

/**
 * Update the chat input's name-list portion in place, preserving whatever
 * the user typed before and after it.
 *
 * `anchor` + `expectedAtAnchor` together answer "is the text still where we
 * left it?" — `anchor` is the range, `expectedAtAnchor` is what we last put
 * there. If nothing has touched that range since (the common case: the user
 * typed a prefix, then a suffix, and only clicked pills in between), we
 * splice `next` in at the same spot. Otherwise — the very first pill click
 * (no anchor yet), or the user having edited into/around the name list — we
 * fall back to appending `next` at the end rather than guessing where it
 * used to be, which is a fine outcome for a case that should be rare and
 * never a corrupted sentence.
 */
export function spliceNameSelection(
  current: string,
  anchor: NameListAnchor | null,
  expectedAtAnchor: string,
  next: string
): { text: string; anchor: NameListAnchor | null } {
  const anchorValid = anchor !== null && current.slice(anchor.start, anchor.end) === expectedAtAnchor;
  if (anchorValid) {
    const before = current.slice(0, anchor.start);
    const after = current.slice(anchor.end);
    if (!next) {
      // Last selected name removed — also collapse the stray double space
      // left behind at the seam.
      return { text: `${before}${after}`.replace(/ {2,}/g, ' '), anchor: null };
    }
    return {
      text: `${before}${next}${after}`,
      anchor: { start: anchor.start, end: anchor.start + next.length },
    };
  }
  if (!next) return { text: current, anchor: null };
  const needsSpace = current.length > 0 && !/\s$/.test(current);
  const insertAt = current.length + (needsSpace ? 1 : 0);
  return {
    text: `${current}${needsSpace ? ' ' : ''}${next}`,
    anchor: { start: insertAt, end: insertAt + next.length },
  };
}

/**
 * Variant B built from the outline the ADD-IN read, not from anything the model
 * said.
 *
 * The card's rows come out of the document, so the buttons that act on them need
 * nothing from the model. They used to: the row appeared only if the model
 * remembered to call `pill_word_insert_outline`, and on a long document it
 * truncated mid-transcription and never got there — leaving a 171-heading card
 * with a lone Copy underneath it. Copy showed because `injectCopyTextPill`
 * synthesises it from the message with no tool call at all; Insert did not,
 * because it had no such fallback. That asymmetry was the bug.
 *
 * Both pills derive from `outlineRowsToSections`, the same function the handler
 * uses, so the synthesised button and the model-triggered one are identical.
 */
/**
 * True when a model-emitted outline pill carries AUTHORED content.
 *
 * A stub is guidance the model invented — the `[bracketed]` fill-in slots TO-02
 * AC-3 requires — so a pill carrying one is BUILD MODE work and must not be
 * replaced by the document's existing headings. A read outline's stubs are
 * always empty (inventing one would write text that was never in the document),
 * which is what makes this a reliable discriminator.
 */
function hasAuthoredStub(args: Record<string, unknown> | undefined): boolean {
  const sections = Array.isArray(args?.sections) ? args.sections : [];
  return sections.some((s) => {
    const stub = (s as { stub?: unknown } | null)?.stub;
    return typeof stub === 'string' && stub.trim().length > 0;
  });
}

export function outlineInsertPills(outline: WordOutline): Pill[] {
  const sections = outlineRowsToSections(outline.rows);
  return [
    {
      type: 'office_action',
      label: `Insert outline (${sections.length})`,
      handler: 'word.insert_outline',
      args: { sections },
    },
    outlineCopyPill(outline),
  ];
}

/**
 * Copy alone, carrying the outline the add-in read.
 *
 * For a READ-ONLY skill — `/structure` declares only `word_get_document_structure`
 * and `pill_word_copy_text`, no placement pill at all. Two defects met here:
 *
 * 1. It had NO pills when the model didn't reach its tool call. `emitted` was
 *    empty, and `maySynthesiseWordPills` refuses a skill that declares no TEXT
 *    pill (`pill_word_copy_text` is kind 'copy'), so the content-synthesis path
 *    bailed too and the card rendered with nothing under it.
 * 2. When the model DID reach it, `ui.pill.word.copy_text` derived an
 *    "Insert outline (N)" button from the copy text — that handler recovers
 *    Variant B for `/outline` by parsing the text, and `/structure`'s text is an
 *    outline, so a read-only command offered to WRITE the document's own
 *    headings back into it.
 *
 * Computing Copy here fixes both: it exists whether or not the model called
 * anything, and Insert is never offered by a skill that cannot place text.
 */
export function outlineCopyPill(outline: WordOutline): Pill {
  const sections = outlineRowsToSections(outline.rows);
  return {
    type: 'office_action',
    label: 'Copy',
    handler: 'word.copy_text',
    args: { text: outlineSectionsToMarkdown(sections) },
  };
}

/**
 * The native Contents field, with no heading restyle. Same label and handler
 * as `ui.pill.word.insert_toc` emits when the model passes no `changes`, so the
 * computed and model-emitted buttons are the same button.
 */
export function outlineTocPill(): Pill {
  return { type: 'office_action', label: 'Insert table of contents', handler: 'word.insert_toc', args: {} };
}

/**
 * Variant B from the COMPUTED outline — no tool call required. The takeover
 * shape for skills whose ANSWER is the outline, or null to fall through to the
 * normal flow.
 *
 * Three shapes, decided by what the skill is ALLOWED to do rather than by what
 * its prose looks like:
 *   declares pill_word_insert_outline  -> Insert outline (N) + Copy      (/outline)
 *   declares pill_word_insert_toc      -> Insert table of contents + Copy (/toc)
 *   declares only pill_word_copy_text  -> Copy alone                     (/structure)
 * A read-only skill must never be offered a write, however outline-shaped its
 * reply is — that was the bogus "Insert outline (N)" on /structure.
 *
 * !declaresTextPill: the takeover is ONLY for skills with no text-placement
 * pill (/structure, /outline READ MODE). A skill that places text merely READ
 * the structure on the way to its real answer; stripping its placement pills
 * here is what turned /format's Variant C into a lone Copy. Same condition the
 * outline card render uses.
 */
function outlineVariantPills(
  actionPills: Pill[],
  hasSelection: boolean,
  allowedTools: string[] | undefined,
  wordOutline: WordOutline | undefined,
  committable?: boolean
): Pill[] | null {
  if (!wordOutline || wordOutline.rows.length === 0) return null;
  const declaresInsertOutline = allowedTools?.includes('pill_word_insert_outline') ?? false;
  const declaresInsertToc = allowedTools?.includes('pill_word_insert_toc') ?? false;
  const declaresOutlineCopy = allowedTools?.includes('pill_word_copy_text') ?? false;
  if (
    (!declaresInsertOutline && !declaresInsertToc && !declaresOutlineCopy) ||
    declaresTextPill(allowedTools ?? [])
  ) {
    return null;
  }
  // An AUTHORED outline wins and falls through to the normal flow: a pill whose
  // sections carry a stub is BUILD MODE work — invented section names with
  // [bracketed] slots — and replacing it with the document's existing headings
  // would discard exactly what the user asked for (TO-02 AC-2 / AC-3).
  //
  // Gated on the skill being able to insert an outline AT ALL. A stub is not
  // proof of authorship on its own: `ui.pill.word.copy_text` recovers a pill by
  // running `parseOutlineTextToSections` over the copy TEXT, and that function
  // attaches any non-heading line it meets as the previous section's stub. So a
  // /structure reply produced a pill with derived stubs, this guard read it as
  // authored, and the whole branch skipped itself — leaving the bogus
  // "Insert outline (71)" on screen next to a 171-row card. A skill that cannot
  // insert an outline cannot have authored one.
  const authoredOutline =
    declaresInsertOutline &&
    actionPills.some(
      (p) => p.type === 'office_action' && p.handler === 'word.insert_outline' && hasAuthoredStub(p.args)
    );
  if (authoredOutline) return null;
  // A stub-less model-emitted outline pill is REPLACED, not merged. Its
  // `sections` came from the model retyping the headings and may be
  // truncated, while these come from the rows the card is rendering. Merging
  // would not save us either: dedupePills keys on the LABEL, so
  // "Insert outline (71)" and "Insert outline (171)" both survive it and the
  // user gets two buttons with different counts.
  //
  // Placement pills are withheld from Variant B (§5: "Insert/Replace/Append
  // are deliberately withheld from A and B"). Enforced here rather than left
  // to the fact that word_outline happens to be the only skill declaring
  // pill_word_insert_outline — a whitelist is not a guarantee.
  const kept = actionPills.filter(
    (p) =>
      p.type !== 'office_action' ||
      (p.handler !== 'word.insert_outline' &&
        p.handler !== 'word.insert_toc' &&
        p.handler !== 'word.copy_text' &&
        !WORD_WRITE_PILL_HANDLERS.has(p.handler))
  );
  // Insert first, then Copy, then anything else — the documented Variant B
  // order, which this function owns because its caller returns before
  // orderWordPills.
  //
  // committable:false wins over the computed WRITE: the backend cannot send it
  // for a skill declaring pill_word_insert_outline today (that tool is in
  // WORD_COMMITTING_PILL_TOOLS), but the flag's contract — nothing placeable —
  // must hold even if that changes. Copy is a clipboard action, not a
  // placement, so it survives the flag; that is /structure's entire bar. The
  // flag is applied HERE, not to declaresInsertOutline above, so the
  // authoredOutline exemption keeps its meaning — an authored pill still falls
  // through to the flag's own path, where emitted pills are kept as-is.
  const canInsert = declaresInsertOutline && committable !== false;
  const canInsertToc = declaresInsertToc && committable !== false;
  let computed: Pill[];
  if (canInsert) {
    computed = outlineInsertPills(wordOutline);
  } else if (canInsertToc) {
    // /toc (AIVA-8029). The skill reads the structure, the card renders it,
    // and this branch then rebuilt the row from the outline — which, for a
    // skill declaring neither pill_word_insert_outline nor a text pill, meant
    // Copy alone. The one pill the user actually came for was dropped. The
    // Insert button is computed here like the other two shapes, so it exists
    // whether or not the model reached its pill_word_insert_toc call. A pill
    // the model DID emit wins: its `changes` carry the heading restyle the
    // model inferred, which the computed pill (native field alone) cannot.
    const emittedToc = actionPills.find((p) => p.type === 'office_action' && p.handler === 'word.insert_toc');
    computed = [emittedToc ?? outlineTocPill(), outlineCopyPill(wordOutline)];
  } else {
    computed = [outlineCopyPill(wordOutline)];
  }
  return markPillsNeedingSelection(dedupePills([...computed, ...kept]), hasSelection);
}

export function buildWordDocPills(
  actionPills: Pill[],
  hasSelection: boolean,
  msgContent: string,
  allowedTools?: string[],
  committable?: boolean,
  wordOutline?: WordOutline
): Pill[] {
  const built = buildWordDocPillsUnfiltered(actionPills, hasSelection, msgContent, allowedTools, committable, wordOutline);
  // A clarifying question or a scope decline (AIVA-8606) has nothing to place
  // AND nothing worth copying. Filtered on the way OUT, not the way in: several
  // branches below re-synthesise placement pills and Copy from the declared
  // tools, so an input filter was undone by the time the bar rendered. Every
  // other pill — a choice, an Open draft, a review apply — is kept as built.
  if (!isQuestionOrDecline(msgContent)) return built;
  return built.filter(
    (p) => !(p.type === 'office_action' && (WORD_WRITE_PILL_HANDLERS.has(p.handler) || p.handler === 'word.copy_text'))
  );
}

function buildWordDocPillsUnfiltered(
  actionPills: Pill[],
  hasSelection: boolean,
  msgContent: string,
  allowedTools?: string[],
  committable?: boolean,
  wordOutline?: WordOutline
): Pill[] {
  // A table pill whose payload is a dump gets no button anywhere on this turn
  // (AIVA-8038, isDegenerateTable). Dropped HERE rather than in the table
  // branch below: the text-pill flow further down reuses this same array, so a
  // filter applied only to the table branch left the button in place via the
  // other route.
  actionPills = actionPills.filter((p) => !isWordTablePill(p) || !isDegenerateTable(tableFromPill(p)));

  // The backend said this turn has nothing to place. The stream-time fallback
  // already honours this; honouring it HERE TOO is the point — two build sites
  // enforcing different rules is what let /clarity render placement buttons its
  // skill never declared. What the model actually emitted is kept: only the
  // synthesis is suppressed, exactly as on the stream path.
  if (committable === false) {
    // /structure lands here on EVERY turn, by design: it declares only
    // pill_word_copy_text, which is correctly absent from the backend's
    // WORD_COMMITTING_PILL_TOOLS (copying places nothing in the document). The
    // computed outline Copy is likewise a clipboard action, not a commit, so
    // the flag must not suppress it — returning early without consulting it is
    // what left /structure with ZERO pills once its prompt stopped calling pill
    // tools. This cannot conjure a write onto a non-committable turn: a skill
    // able to compute an Insert here declares pill_word_insert_outline, which
    // IS in the committing set, so such a turn is committable by definition.
    //
    // Consulted ONLY when the emitted set is empty or carries a derived
    // word.insert_outline (the truncated "Insert outline (71)" a stale prompt's
    // copy call produces — that one must be replaced). An emitted plain Copy is
    // kept as-is: /gaps and /risks share /structure's exact tool signature, and
    // their Copy carries their FINDINGS — replacing it with the outline would
    // hand the user the wrong clipboard on a spontaneous structure read.
    const emittedDerivedInsert = actionPills.some(
      (p) => p.type === 'office_action' && p.handler === 'word.insert_outline'
    );
    if (actionPills.length === 0 || emittedDerivedInsert) {
      const outlineVariant = outlineVariantPills(actionPills, hasSelection, allowedTools, wordOutline, committable);
      if (outlineVariant) return outlineVariant;
    }
    return markPillsNeedingSelection(resolveWordPillConflicts(dedupePills([...actionPills])), hasSelection);
  }
  const plainText = toPlainPillText(msgContent);

  // Variant A — the review contract. Tested BEFORE the findings guard below, and
  // that order is the point: the guard drops everything because corrections
  // cannot be recovered from free-text findings, which stops being true the
  // moment the payload parses. A MALFORMED payload returns null here and falls
  // through to the guard and the default bar, exactly as AC-5 asks.
  const reviewChanges = reviewPayloadFor(msgContent, allowedTools);
  if (reviewChanges) {
    return markPillsNeedingSelection(
      buildReviewVariant(actionPills, reviewChanges, msgContent, allowedTools),
      hasSelection
    );
  }

  // Variant B from the COMPUTED outline — no tool call required (see
  // outlineVariantPills). Tested BEFORE the findings guard below for the same
  // reason Variant A is: that guard drops pills because content cannot be
  // recovered from free-text prose, which stops being true the moment the
  // payload is already in hand. READ MODE's one-liner ("171 headings, none
  // carry Word heading styles") is exactly the shape that guard is looking for,
  // so ordering it after would have dropped the buttons on the very replies
  // this exists to serve.
  const outlineVariant = outlineVariantPills(actionPills, hasSelection, allowedTools, wordOutline, committable);
  if (outlineVariant) return outlineVariant;

  // A findings/report reply ("## No Inconsistencies Found"), or an error /
  // guidance nudge ("Sorry — I couldn't complete that", "The document is
  // empty"), carries no insertable content. Drop every pill that would paste
  // content into the document — text inserts and tables — even when the model
  // emitted them itself, and synthesise none: clicking one would overwrite the
  // user's selection with the report or the error notice (AIVA-6914). Payload-
  // bearing review pills (apply_review, insert_comment, highlight, …) carry
  // real corrections and are kept. Runs before the table branch so a findings
  // reply that merely CONTAINS a markdown table grows no table pills either.
  if (isNonInsertableOrFindings(msgContent)) {
    const kept = actionPills.filter(
      (p) => !(p.type === 'office_action' && WORD_WRITE_PILL_HANDLERS.has(p.handler))
    );
    return resolveWordPillConflicts(dedupePills(kept));
  }

  // A degenerate payload is dropped BEFORE it counts as "the model emitted
  // table pills", so a dump falls through to the text-pill flow (and, for a
  // decline-shaped reply, to no pills at all) instead of rendering Insert /
  // Replace table over one padded row — AIVA-8038, isDegenerateTable.
  const tablePills = actionPills.filter(isWordTablePill);
  const hasTablePills = tablePills.length > 0;
  // Synthesising table pills from a markdown table in the CONTENT is only
  // legitimate when the skill actually declares the table pill tool. A skill
  // whose output merely CONTAINS a table (e.g. /template's sample table) must
  // not have its real pills (Copy) hijacked by synthesised table pills.
  const maySynthesiseTablePills = !allowedTools || allowedTools.includes('pill_word_insert_table');
  const parsed = hasTablePills || !maySynthesiseTablePills ? null : extractTableData(msgContent);
  // Same bar for a table recovered from the markdown as for one the model
  // passed as pill args: a dump gets no buttons synthesised for it either.
  const tableFromContent = isDegenerateTable(parsed) ? null : parsed;
  if (hasTablePills || tableFromContent) {
    let enriched: Pill[];
    if (hasTablePills) {
      enriched = tablePills;
    } else {
      const args = { headers: tableFromContent!.headers, rows: tableFromContent!.rows };
      enriched = [
        {
          type: 'office_action',
          label: 'Insert table here',
          handler: 'word.insert_table',
          args: { ...args, insert_position: 'at_cursor' },
        },
        {
          type: 'office_action',
          label: 'Add table to end',
          handler: 'word.insert_table',
          args: { ...args, insert_position: 'append_to_end' },
        },
      ];
    }
    // Injected unconditionally, exactly like injectReplaceTextPill below: the
    // pill stays on screen and markPillsNeedingSelection greys it when there is
    // no selection. Gating the INJECTION on hasSelection is what made the row
    // change shape as the user clicked around the document.
    injectReplaceTablePill(enriched);
    // Copy is declared by word_table (pill_word_copy_text) and was being
    // dropped here because the branch returned table pills only.
    injectCopyTextPill(enriched, plainText, allowedTools);
    return markPillsNeedingSelection(
      orderWordTablePills(resolveWordPillConflicts(dedupePills(enriched))),
      hasSelection
    );
  }

  // Text-pill flow. Insert at cursor and Append to end don't depend on the
  // selection, so they're always injected; only Replace selection toggles on
  // hasSelection. Injecting first (before the Replace toggle) keeps the two
  // states symmetric so deselecting doesn't drop a pill the hasSelection
  // branch was synthesising on the fly (AIVA-6049).
  //
  // The injection helpers each check `allowedTools` — a skill like /grammar
  // that declares only pill_word_replace_selection will not get Insert or
  // Append synthesised, matching the backend's whitelist exactly.
  // (The findings / non-insertable guard has already returned above, so by here
  // the response is known to carry insertable content.)
  // Reconcile BEFORE the injections: injectReplaceTextPill and injectCopyTextPill
  // copy their text off the first payload pill they find, so a stale argument
  // here would be propagated into every button in the row.
  const enriched = reconcilePayloadPillsWithMessage([...actionPills], plainText);
  injectInsertAndAppendPills(enriched, plainText, allowedTools);
  injectReplaceTextPill(enriched, plainText, allowedTools);
  // Copy last, so it sits at the end of the row and after the write pills have
  // resolved their text.
  injectCopyTextPill(enriched, plainText, allowedTools);
  // Both placement buttons stay. Replace selection is greyed when there is no
  // selection — the specification's Variant C, and the mockup's rendering. It is
  // deliberately NOT collapsed or swapped: a control that vanishes gives the user
  // nothing to learn from, whereas a disabled one with a tooltip says what to do.
  //
  // Order last, so the row reads in the documented order whatever mix of
  // model-emitted and synthesised pills got us here; and collapse first, so the
  // ordering pass never has to reconcile three relabelled outline buttons.
  return markPillsNeedingSelection(
    orderWordPills(collapseOutlinePills(resolveWordPillConflicts(dedupePills(enriched)), msgContent)),
    hasSelection
  );
}

/** Grey out pills whose spec says they need a selection, when there isn't one.
 *
 *  Slice 3: the requirement already lives on the spec (`needsSelection`) and the
 *  zero-pill fallback already honoured it, but `buildWordDocPills` re-derived it by
 *  hand in three places — an `if (hasSelection)` around the Replace injection, a
 *  filter stripping a stale `word.replace_selection`, and the same question again
 *  again when collapsing the two placement pills into one. Three spellings of
 *  one rule is how they drift — the collapse is gone; this is the only gate.
 *
 *  It MARKS rather than removes: per the specification (§5, Variant C) and the
 *  mockup, Replace selection stays on screen greyed with "Select text in Word
 *  first", so the user learns the prerequisite instead of watching a button
 *  appear and disappear as the selection changes. */
export function markPillsNeedingSelection(pills: readonly Pill[], hasSelection: boolean): Pill[] {
  if (hasSelection) return [...pills];
  return pills.map((pill) => {
    if (pill.type !== 'office_action') return pill;
    if (!pillNeedsSelection(pill)) return pill;
    return { ...pill, disabled: true, disabledReason: 'Select text in Word first' };
  });
}

export function extractTableData(content: string): { headers: string[]; rows: string[][] } | null {
  return parseMdTableFromLines(content.split('\n'));
}

/** Header names the model reaches for when the material had no columns in it. */
const PLACEHOLDER_HEADER = /^(?:column|header|col)\s*\d*$|^(?:item|content|field|information|n\/a)$/i;

/**
 * Whether a table payload is a dump rather than a table (AIVA-8038).
 *
 * The prompt tells /table to decline material with no table in it, and mostly
 * it does — but the Conversational tier resolves to a model that does not
 * support `temperature` (see `_supports_temperature` in shared/llm_factory.py),
 * so the skill document's `temperature: 0.0` is dropped and the judgement is
 * re-sampled on every run. Measured over the reported selections it declines
 * reliably, then occasionally does not. A prompt cannot close that gap; this
 * can, because every failure observed has a signature visible in the PAYLOAD:
 *
 *   one data row      "any employee approving a commitment of any value" ->
 *                     Header 1 | Header 2, one row, second cell empty
 *   padded row        "category owners and their delegates" repeated into BOTH
 *                     cells of its row, because there was no second fact
 *   placeholder head  Column 1 | Column 2, Item | Details with a cell "Content"
 *
 * Used by every path that can put an Insert/Replace button on screen. It does
 * not remove the model's markdown from the chat log — it removes the BUTTONS,
 * which is where the damage is: clicking Replace table swaps the user's real
 * clause for a one-cell table of itself.
 */
export function isDegenerateTable(table: { headers: string[]; rows: string[][] } | null): boolean {
  if (!table) return true;
  const { headers, rows } = table;
  if (headers.length < 2 || rows.length < 1) return true;
  const placeholderHeaders = headers.filter((h) => !h.trim() || PLACEHOLDER_HEADER.test(h.trim()));
  // Every header a placeholder. ONE generic header is legitimate — "Term |
  // Details" is a good key/value pair — so this fires only when nothing in the
  // header row carries meaning.
  if (placeholderHeaders.length === headers.length) return true;
  // A cell that is empty, or is itself a placeholder word. The Item | Details
  // failure put the literal string "Content" in the first cell.
  if (rows.some((row) => row.some((c) => !c.trim() || PLACEHOLDER_HEADER.test(c.trim())))) return true;
  // Padded rows: the same value repeated across columns means the row never
  // had that many facts in it. Half the rows padded is a dump.
  const padded = rows.filter((row) => {
    const cells = row.map((c) => c.trim().toLowerCase());
    return new Set(cells).size < cells.length;
  });
  if (padded.length * 2 >= rows.length) return true;
  // A single data row is legitimate on its own (a one-line summary table), but
  // not alongside a placeholder header — that combination is every reported
  // failure. Two real headers over one real row stays allowed.
  return rows.length < 2 && placeholderHeaders.length > 0;
}

/**
 * Drop markdown table blocks from a reply that is still streaming.
 *
 * `allowedTools` and `skillKey` arrive only on the `done` event, so while the
 * tokens are still coming there is no way to know this is a /table turn and
 * degenerateTableDecline cannot run yet. Without this the user watched the
 * dump render in full ("Column 1 | Column 2 | external suppliers | …") and
 * then be replaced by the decline a second later — the fix looked like a
 * glitch (AIVA-8038).
 *
 * Evaluating degeneracy DURING the stream is not the answer: a real table
 * arrives a row at a time and looks like a one-row dump until its second row
 * lands, so the content would flip the other way instead. Holding the table
 * until the turn is complete flickers in neither direction, and the
 * "Preparing the table…" status already says work is happening.
 */
export function stripStreamingTables(content: string): string {
  if (!content.includes('|')) return content;
  return content
    .split('\n')
    .filter((line) => !/^\s*\|/.test(line))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trimEnd();
}

/** What the user sees instead of a table we refuse to offer buttons for. */
export const DEGENERATE_TABLE_DECLINE =
  "No table structure found in the selected text. There isn't enough here to fill two columns — tell me the " +
  "columns you want, or select a passage that lists several items carrying the same details.";

/**
 * The decline to show in place of a dump, or null to render the reply as sent.
 *
 * The skill prompt is supposed to decline material with no table in it, and in
 * isolation it does. In the real turn the pill tools are BOUND to the model,
 * and that alone flips it: measured on "the shared services team processing
 * invoices", 3/3 correct declines with no tools attached, 3/3 wrong BUILDS with
 * them. Adding a prompt rule that the tool's presence is not a reason to call
 * it moved 24 samples to 9/24. The model reaches for an available tool, and no
 * wording reliably stops it.
 *
 * So the last word is taken away from the model. isDegenerateTable already
 * stops the BUTTONS; without this the user still reads a two-column table of
 * one split phrase and reasonably concludes nothing was fixed (AIVA-8038).
 *
 * Deliberately replaces the WHOLE reply rather than snipping the table out: the
 * prose around a dump is its lead-in ("Here is the table:"), which is worse than
 * useless once the table is gone.
 */
export function degenerateTableDecline(content: string, allowedTools?: string[]): string | null {
  if (!allowedTools?.includes('pill_word_insert_table')) return null;
  const table = extractTableData(content);
  if (!table || !isDegenerateTable(table)) return null;
  return DEGENERATE_TABLE_DECLINE;
}

/** The table a pill would insert, for degeneracy checking. */
function tableFromPill(p: Pill): { headers: string[]; rows: string[][] } | null {
  if (p.type !== 'office_action') return null;
  const headers = p.args?.headers;
  const rows = p.args?.rows;
  if (!Array.isArray(headers) || !Array.isArray(rows)) return null;
  return { headers: headers.map(String), rows: rows.map((r) => (Array.isArray(r) ? r.map(String) : [])) };
}

/**
 * Zero-pill fallback (AIVA-5927): the model wrote a Word response but skipped
 * its pill tool calls entirely — synthesise the pills the skill declared so
 * the user can still apply the content. Strictly gated by the turn's
 * `allowed_tools` from the done event: no allowedTools (old stream, non-Word
 * turn) or no text/table pill tools declared → nothing is synthesised, so
 * report-only skills (risks, gaps, passive) can never grow phantom pills.
 * Payload-bearing pills (apply_review, insert_comment, …) are never
 * synthesised — their args only the LLM can produce.
 */
// Skill-key based synthesis exclusion was removed after AIVA-5927: word_multi
// was the only entry, and swallowing all pills for that skill caused a worse
// UX than the findings-heading guard (which now filters review-only responses
// content-first, not skill-first). Re-introduce a Set<string> here if a future
// skill genuinely must NEVER receive fallback pills regardless of content.

/** Findings-heading vocabulary the LLM uses for review/analysis output.
 *  If the response opens with one of these (as a #/##/### heading OR as
 *  **bold** OR as a plain leading line), the fallback must NOT synthesise
 *  pill_word_replace_selection — that pill would swap the user's selected
 *  text with the FINDINGS report instead of applying corrections (the LLM
 *  should have called pill_word_apply_review with a changes array, but
 *  forgot). AIVA-5927 findings-response guard. */
const FINDINGS_HEADING_LABELS = [
  'Passive Voice',
  'Clarity Issues?',
  'Risk Findings',
  'Risk Summary',
  'Risky Clauses',
  'Flagged Clauses',
  'Grammar Fixes?',
  'Grammar Suggestions',
  'Grammar Issues',
  'Term Inconsistencies',
  'Defined Term Inconsistencies',
  'Inconsistencies Found',
  'Gaps',
  'Missing Information',
  'Findings',
  'Spelling Mistakes?',
  'Unclear Sentences',
  'Ambiguous Sentences',
];
const FINDINGS_HEADING_PATTERN = new RegExp(
  // Match the label as: a markdown heading (#/##/### + optional space),
  // a bold wrapper (**Label**), or a plain leading line — anywhere in the
  // first ~200 chars of the response (leading whitespace tolerated).
  //
  // `No\s+` is optional in front of the label because the NEGATIVE result is
  // the most important case to catch: "## No Inconsistencies Found" and
  // "## No Gaps" are reports that there is nothing to apply, so a synthesised
  // Replace pill would overwrite the user's selection with the words "No
  // Inconsistencies Found". Without this the "No " sat between the heading
  // marker and the label and the whole pattern missed.
  String.raw`(?:^|\n)\s*(?:#{1,4}\s*|\*\*)?(?:No\s+)?(?:` +
    FINDINGS_HEADING_LABELS.join('|') +
    String.raw`)\b`,
  'i'
);

/** Prose form of a nothing-found result — "No inconsistencies were found in the
 *  excerpt provided.", "No defined-term variations appear." The label pattern
 *  above can't catch these because the words it keys on are split ("…
 *  inconsistencies WERE found").
 *
 *  Deliberately excludes "required": legal drafting routinely opens "No Party
 *  shall be required to…", which is insertable content, not a findings report.
 *  A wrongly-suppressed pill is recoverable; a wrongly-offered one overwrites
 *  the user's text. */
const NO_FINDINGS_PROSE_PATTERN =
  /^\s*(?:#{1,4}\s*|\*\*)?No\b[^\n]{0,70}?\b(?:found|identified|detected|flagged|appears?|present|needed|to\s+(?:apply|change|report|fix))\b/i;

function isFindingsResponse(content: string): boolean {
  const head = content.slice(0, 400);
  return FINDINGS_HEADING_PATTERN.test(head) || NO_FINDINGS_PROSE_PATTERN.test(head.trim());
}

/**
 * A review that found nothing — the verdict, not content to place.
 *
 * `{"review":[]}` is a real answer ("I looked, there is nothing"), and
 * reviewPayloadFor already declines to build an Apply button for it (AC-3).
 * What it could not do is stop the turn falling through to the TEXT-pill flow,
 * so a clean review rendered Insert at cursor / Replace selection / Append to
 * end — and clicking Replace selection pasted the sentence "The document is
 * clean on clarity, tone, and formatting." over the user's own text.
 *
 * Keyed on the PAYLOAD first, which is exact, and on the prose only as a
 * fallback for a clean verdict written without one. The prose forms are narrow
 * on purpose: "clean" and "nothing to flag" next to a document/selection noun,
 * not the bare words, so a drafted clause about a clean-hands covenant is not
 * mistaken for a verdict.
 */
function isCleanReviewVerdict(content: string): boolean {
  if (parseReviewSuggestions(content).status === 'clean') return true;
  const head = content.trim().slice(0, 240);
  return CLEAN_VERDICT_PROSE.some((re) => re.test(head));
}

const CLEAN_VERDICT_PROSE: RegExp[] = [
  // "The document is clean on clarity, tone, and formatting."
  /\b(?:document|selection|text|paragraph)\b[^\n]{0,40}\bis clean\b/i,
  // REVIEW_CLEAN_ONLY_REPLY, and the shapes the review prompts ask for.
  /\bnothing to flag\b/i,
  /\bfound nothing\b/i,
  /\bno (?:issues|inconsistencies|problems|findings)\b/i,
];

/**
 * True when a response carries no insertable document content — either a
 * findings/report ("## No Inconsistencies Found") or an error/guidance nudge.
 *
 * **Every** text-pill synthesis path must consult this. There are three, and
 * they were guarded inconsistently: `turnEvents.emitWordActionPillsFallback`
 * checked only `isNonInsertableResponse`, so a findings report still got
 * Replace / Insert / Append pills — and clicking one pasted the report over the
 * user's own text. Shared predicate rather than three re-derivations.
 */
export function isNonInsertableOrFindings(content: string): boolean {
  return isNonInsertableResponse(content) || isFindingsResponse(content) || isCleanReviewVerdict(content);
}

/**
 * A clarifying question or a scope decline (AIVA-8606) — see
 * {@link isClarifyingQuestion} and {@link isScopeDecline}.
 *
 * Deliberately NOT part of {@link isNonInsertableResponse}. That predicate gates
 * EVERY pill on the message in ChatMessages, and a question is exactly the reply
 * that carries choice pills: "Which email would you like to paste?" over the
 * email picker, "Which John did you mean?" over the name candidates. Folding it
 * in there would have deleted those choices. It belongs only on the Word
 * document-pill paths: buildWordDocPills (placement + Copy dropped, every other
 * pill kept), synthesis, and the two stream-time fallbacks in turnEvents. It is
 * kept out of isNonInsertableOrFindings too, because buildWordDocPills' findings
 * branch returns early — a paste-email question routed there would skip the
 * normal ordering and selection marking of its email pills.
 */
export function isQuestionOrDecline(content: string): boolean {
  return isClarifyingQuestion(content) || isScopeDecline(content);
}

/**
 * Whether this turn may have placement pills synthesised for it at all.
 *
 * Four independent reasons to decline, collected here rather than inline so
 * synthesizeWordDocPills reads as "may we? then build" — the guards are the
 * policy and the loop below is the mechanism, and Sonar counted the two
 * together as one over-complex function.
 *
 * The narrowing return type is deliberate: a true result also tells the caller
 * allowedTools is present, so it does not re-check.
 */
function maySynthesiseWordPills(
  allowedTools: string[] | undefined,
  msgContent: string,
  committable?: boolean
): allowedTools is string[] {
  // Same gate as the stream-time fallback and buildWordDocPills — one rule, three
  // entry points, so a fix can no longer land on only part of the path.
  if (committable === false) return false;
  if (!allowedTools || !declaresSynthesisablePill(allowedTools)) return false;
  // A skill that declares no TEXT placement pill must never have one synthesised,
  // whatever its name or the shape of its prose (see declaresTextPill).
  if (!declaresTextPill(allowedTools) && !allowedTools.includes('pill_word_insert_table')) return false;
  // Findings-only or non-insertable response: the LLM produced a review report
  // (Passive Voice / Grammar Fixes / Risk Findings / "No Inconsistencies
  // Found"), or an error/guidance nudge. Clicking a synthesised Replace pill
  // would dump that into the doc, replacing the original text with analysis
  // prose — worse than showing no pill at all. Return false so the user reads
  // the findings and applies changes manually. The correct pill for these turns
  // (pill_word_apply_review with a changes array) can only be produced by the
  // LLM — we can't recover its structured pairs from the free-text findings.
  return !isNonInsertableOrFindings(msgContent) && !isQuestionOrDecline(msgContent);
}

/** The placement pills a skill declares, in spec-table order. */
function basePlacementPills(hasSelection: boolean, allowedTools: string[], plainText: string): Pill[] {
  const base: Pill[] = [];
  for (const spec of WORD_PILL_SPECS) {
    if (spec.kind !== 'text') continue;
    if (spec.needsSelection && !hasSelection) continue;
    if (!allowedTools.includes(spec.toolId)) continue;
    // Built through textPillFor rather than from spec.label directly: this was
    // the fourth site constructing a placement pill, and the only one that
    // never applied the outline label — so a synthesised outline row read
    // "Insert at cursor" while every other path read "Insert outline (N)".
    const pill = textPillFor(spec.handler, plainText);
    if (pill) base.push(pill);
  }
  return base;
}

export function synthesizeWordDocPills(
  hasSelection: boolean,
  msgContent: string,
  allowedTools?: string[],
  _skillKey?: string,
  committable?: boolean
): Pill[] {
  // The review contract is the one case where a zero-pill findings reply DOES
  // have something to offer, so it is tested before maySynthesiseWordPills
  // declines — that gate exists for prose findings, and a parsed payload is not
  // prose. `committable === false` still wins: the backend said nothing about
  // this turn may be placed.
  if (committable !== false && reviewPayloadFor(msgContent, allowedTools)) {
    return buildWordDocPills([], hasSelection, msgContent, allowedTools, committable);
  }
  if (!maySynthesiseWordPills(allowedTools, msgContent, committable)) return [];

  const plainText = toPlainPillText(msgContent);
  if (!plainText) return [];

  const base = basePlacementPills(hasSelection, allowedTools, plainText);
  // buildWordDocPills also covers the table-only case: with an empty base it
  // can still synthesise table pills from a markdown table in the content
  // (gated on pill_word_insert_table), and it applies the usual dedupe +
  // conflict-guard + no-selection filtering to whatever we pass it.
  return buildWordDocPills(base, hasSelection, msgContent, allowedTools);
}

const NON_INSERTABLE_ERROR_PATTERNS: RegExp[] = [
  /something went wrong/i,
  /your sign-?in has expired/i,
  /session got into a bad state/i,
  /isn'?t available for your account/i,
  /busy right now/i,
  /can'?t reach aiva/i,
  /temporarily unavailable/i,
  /couldn'?t process this request/i,
  // The backend's empty-turn fallback (`_EMPTY_TURN_FALLBACK` in
  // shared/agent_base.py): "Sorry — I couldn't complete that. Please try
  // again…". The model produced nothing usable, so the reply is an apology,
  // not insertable content — it must not carry document pills (AIVA-6914).
  /couldn'?t complete that/i,
  /can'?t run here/i,
  /can'?t help with that/i,
];

const GUIDANCE_PATTERNS: RegExp[] = [
  /\bplease select\b/i,
  /\bselect the entire (document|text)\b/i,
  /\bctrl\s*\+\s*a\b/i,
  /\bselect (some|the) text\b/i,
  /\bhighlight the (text|content)\b/i,
  // /toc (and other document skills) on an empty document: "The document is
  // empty — add some content first…". A nudge, not insertable content — pills
  // pointing at it have nothing to place (AIVA-6914).
  /\bdocument is empty\b/i,
];

/** A reply that asks the user for more information instead of doing the task —
 *  "I need the formatting issues you want fixed (e.g., heading levels, …)."
 *  Its prose is a question TO the user, so Insert / Replace / Append would paste
 *  the question into the document and Copy would put it on the clipboard: none
 *  of the four has anything to act on (AIVA-8606).
 *
 *  Anchored at the START of the reply and capped at
 *  {@link CLARIFYING_QUESTION_MAX_LENGTH}: a drafted letter or clause may contain
 *  a question ("Could you confirm the completion date?") but does not open with
 *  one addressed to the user and stop. A wrongly-suppressed pill is recoverable
 *  (the user asks again); a wrongly-offered one writes the question into the
 *  document. */
const CLARIFYING_QUESTION_PATTERNS: RegExp[] = [
  // "I need the formatting issues you want fixed…", "I'll need to know which…"
  /^I(?:'ll| will)?\s+need\b[^\n]{0,80}\byou(?:r)?\b/i,
  // "To help with that, I need…", "Before I start, could you…"
  /^(?:to|before)\b[^,\n]{0,60},\s*(?:I(?:'ll| will)?\s+need|could you|can you|please)\b/i,
  // "Could you tell me which…", "Can you clarify…", "Would you please specify…"
  /^(?:could|can|would)\s+you\s+(?:please\s+)?(?:tell|clarify|specify|share|confirm|let me know|provide|describe|explain|point out|list)\b/i,
  // "Please specify which…", "Kindly let me know…", "Let me know which issues…"
  /^(?:please|kindly)\s+(?:tell|clarify|specify|share|confirm|let me know|provide|describe|explain|point out|list)\b/i,
  /^let me know\s+(?:which|what|how|where|whether|if)\b/i,
  // "Which formatting issues would you like me to fix?", "What should I change?"
  /^(?:what|which)\b[^\n?]{0,120}\b(?:would you like|do you want|should I|did you mean)\b/i,
  // "Do you want me to…?", "Did you mean…?"
  /^(?:do|did|would)\s+you\s+(?:want|mean|like|prefer)\b/i,
];

const CLARIFYING_QUESTION_MAX_LENGTH = 400;

export function isClarifyingQuestion(content: string): boolean {
  const text = content.trim().replace(/^\*\*|\*\*$/g, '');
  if (!text || text.length > CLARIFYING_QUESTION_MAX_LENGTH) return false;
  return CLARIFYING_QUESTION_PATTERNS.some((re) => re.test(text));
}

/** A reply that declines or states its own scope instead of producing content —
 *  "I can only help with editing the selected text.", "I can help only to edit
 *  …", "That isn't something I can do in Word.", "Changing the font isn't
 *  supported." It is the skill talking ABOUT itself, so a placement pill would
 *  paste the refusal into the document (AIVA-8606).
 *
 *  Same shape rules as {@link CLARIFYING_QUESTION_PATTERNS}: anchored at the
 *  start of the reply (an optional "Sorry," / "Unfortunately," lead-in aside)
 *  and capped at {@link CLARIFYING_QUESTION_MAX_LENGTH}.
 *
 *  Every pattern also needs a verb about AiVA's OWN ability (help, edit,
 *  format, support…), because the same openings are ordinary drafting: "I can
 *  only apologise for the delay", "I am unable to attend the hearing", "The
 *  claim is not supported by the evidence" are letters the user asked for, and
 *  they keep their pills. */
const DECLINE_LEAD_IN = String.raw`^(?:(?:sorry|unfortunately|apologies)[,.!—–-]*\s+)?`;
/** What AiVA declines to DO — never a verb a drafted letter opens with. */
const ABILITY_VERB = String.raw`(?:help|assist|edit|format|change|modify|access|perform|apply|support|handle|work\s+with|answer|do\s+(?:that|this|it))\b`;
const SCOPE_DECLINE_PATTERNS: RegExp[] = [
  // "I can only help with editing…", "I can help only to edit…", "I could only assist…"
  new RegExp(
    DECLINE_LEAD_IN + String.raw`I\s+(?:can|could)\s+(?:only\s+` + ABILITY_VERB + String.raw`|help\s+(?:you\s+)?only\b)`,
    'i'
  ),
  // "I'm only able to help…", "I am not able to change…", "I'm unable to format…",
  // "I'm designed only to edit…"
  new RegExp(
    DECLINE_LEAD_IN +
      String.raw`I(?:'m|’m|\s+am)\s+(?:only\s+able|not\s+able|unable|not\s+designed|designed\s+only|only\s+designed|only\s+here)\s+to\s+` +
      ABILITY_VERB,
    'i'
  ),
  // "I can't change fonts…", "I cannot do that", "I don't support…"
  new RegExp(
    DECLINE_LEAD_IN +
      String.raw`I\s+(?:can(?:'|’)?t|cannot|can\s+not|don(?:'|’)?t|do\s+not|won(?:'|’)?t)\s+(?:be\s+able\s+to\s+)?` +
      ABILITY_VERB,
    'i'
  ),
  // "That isn't something I can do", "This isn't supported", "That's not available here"
  new RegExp(
    DECLINE_LEAD_IN +
      String.raw`(?:that|this)(?:'s|’s|\s+is)?\s*(?:isn(?:'|’)?t|is\s+not|not)\s+(?:something\s+(?:I|AiVA)\s+(?:can|support)|supported|available\s+(?:here|in\s+(?:Word|AiVA)))\b`,
    'i'
  ),
  // "AiVA can only…", "AiVA doesn't support…", "This command can't…"
  new RegExp(
    DECLINE_LEAD_IN +
      String.raw`(?:AiVA|this\s+(?:feature|command|skill|tool))\s+(?:can\s+only|only\s+(?:supports|works)|doesn(?:'|’)?t|does\s+not|isn(?:'|’)?t\s+able|cannot|can(?:'|’)?t)\b`,
    'i'
  ),
  // "Changing the font isn't supported.", "Page borders are not currently supported."
  // The subject must be an ACTION (a gerund) or a styling feature — "The claim
  // is not supported" is a sentence of the user's document, not a refusal.
  new RegExp(
    DECLINE_LEAD_IN +
      String.raw`(?:[a-z]+ing\b|fonts?\b|colou?rs?\b|page\s+borders?\b|watermarks?\b|margins?\b|images?\b)[^\n.]{0,70}\b(?:is|are)(?:n(?:'|’)?t|\s+not)\s+(?:currently\s+|yet\s+)?(?:supported|available)\b`,
    'i'
  ),
];

export function isScopeDecline(content: string): boolean {
  const text = content.trim().replace(/^\*\*|\*\*$/g, '');
  if (!text || text.length > CLARIFYING_QUESTION_MAX_LENGTH) return false;
  return SCOPE_DECLINE_PATTERNS.some((re) => re.test(text));
}

export function isNonInsertableResponse(content: string): boolean {
  const text = content.trim();
  if (!text) return false;
  if (NON_INSERTABLE_ERROR_PATTERNS.some((re) => re.test(text))) return true;
  if (text.length <= 320 && GUIDANCE_PATTERNS.some((re) => re.test(text))) return true;
  return false;
}

function resolveWordPillText(
  pill: Extract<Pill, { type: 'office_action' }>,
  plainTextFallback: string
): string {
  const rawText = typeof pill.args?.text === 'string' ? pill.args.text : '';
  return rawText ? rawText.replace(/\*\*(.*?)\*\*/gs, '$1').replace(/\*(.*?)\*/gs, '$1') : plainTextFallback;
}

function getDropHandler(draftMode: EmailDraftMode | null): string | null {
  if (draftMode === 'compose') return 'compose.reply';
  if (draftMode === 'reply') return 'compose.open';
  return null;
}

export function stripMarkdownForWord(text: string): string {
  return text
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*(.*?)\*\*/gs, '$1')
    .replace(/\*(.*?)\*/gs, '$1')
    .replace(/^[-*+]\s+/gm, '')
    .replace(/^\d+\.\s+/gm, '')
    .replace(/`[^`]*`/g, '')
    .trim();
}

export function prepareRenderedPills(
  rawPills: Pill[],
  assistantContent: string,
  draftMode: EmailDraftMode | null
): Pill[] {
  // Fallback: strip only bold/italic/code — KEEP "- " bullet markers and "# " heading
  // markers so parseWithHeadings() in the Word handler can convert them to proper
  // Word styles (List Bullet, Heading 1/2/3). stripMarkdownForWord removes those
  // markers and must NOT be used here.
  const plainTextFallback = assistantContent
    .replace(/\*\*(.*?)\*\*/gs, '$1')
    .replace(/\*(.*?)\*/gs, '$1')
    .replace(/`[^`]*`/g, '')
    .trim();
  const enriched: Pill[] = [];
  for (const pill of rawPills) {
    if (pill.type === 'office_action' && WORD_TEXT_PILL_HANDLERS.has(pill.handler)) {
      enriched.push({ ...pill, args: { ...pill.args, text: resolveWordPillText(pill, plainTextFallback) } });
    } else if (pill.type === 'office_action' && WORD_TABLE_PILL_HANDLERS.has(pill.handler)) {
      // Enrich table pills with headers/rows parsed from assistantContent when
      // the LLM omits them from the pill args — without this, the insert handler
      // receives empty arrays and silently inserts nothing.
      const tableData = resolveTablePillData(pill, assistantContent);
      enriched.push(
        tableData
          ? { ...pill, args: { ...pill.args, headers: tableData.headers, rows: tableData.rows } }
          : pill
      );
    } else {
      enriched.push(pill);
    }
  }

  // The LLM emits both `compose.reply` and `compose.open` pills (per the
  // email_draft / tone_adjuster skill mandates). Strip whichever doesn't
  // fit the current draft mode so the user doesn't get a redundant button
  // or one that would slam the body into the wrong thread.
  const dropHandler = getDropHandler(draftMode);
  if (!dropHandler) return enriched;
  return enriched.filter((pill) => !(pill.type === 'office_action' && pill.handler === dropHandler));
}

/**
 * Bare affirmatives and negatives, for answering a multipart plan card by
 * typing instead of clicking.
 *
 * Deliberately a closed list of WHOLE answers rather than a prefix or keyword
 * test. "yes" means run the offered step; "yes, but do the alignment first"
 * carries an instruction this cannot honour and must stay an ordinary turn, so
 * anything with more in it than the affirmative itself falls through.
 *
 * Kept small and boring on purpose. The cost of a miss is one extra click; the
 * cost of a false positive is running an edit on the user's document off the
 * back of a sentence that was not consent.
 */
const AFFIRMATIVES = new Set([
  'y',
  'ok',
  'okay',
  'yes',
  'yep',
  'yeah',
  'yup',
  'sure',
  'please',
  'go',
  'go ahead',
  'do it',
  'yes please',
  'sounds good',
  'proceed',
  'continue',
]);

const NEGATIVES = new Set([
  'n',
  'no',
  'nope',
  'nah',
  'stop',
  'cancel',
  'no thanks',
  'no thank you',
  'not now',
  'never mind',
  'nevermind',
]);

/**
 * `"yes"` / `"no"` when *text* is a bare affirmative or negative, else `null`.
 *
 * Trailing punctuation and casing are ignored ("Yes!", "OK." ) since they carry
 * no extra instruction; internal words are not, for the reason above.
 */
/** Trailing `.`, `!` and `,`, removed without a regex.
 *
 *  `/[.!,]+$/` is super-linear: on a string of many dots that does not match,
 *  the engine retries the quantifier from every start position. A backwards
 *  scan is one pass, and the input here is user-typed chat text.
 */
function stripTrailingPunctuation(text: string): string {
  let end = text.length;
  while (end > 0 && '.!,'.includes(text[end - 1]!)) end -= 1;
  return text.slice(0, end);
}

export function affirmativeOrNegative(text: string): 'yes' | 'no' | null {
  const normalized = stripTrailingPunctuation(text.trim().toLowerCase()).replace(/\s+/g, ' ');
  if (!normalized) return null;
  if (AFFIRMATIVES.has(normalized)) return 'yes';
  if (NEGATIVES.has(normalized)) return 'no';
  return null;
}
